import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Loader2, MessageCircle, Trash2, ExternalLink, QrCode, Link2, Eye } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { shareUrl, expiryText, whatsappUrl, copyText } from "@/lib/share";
import { timeAgo } from "@/lib/time";

const EXPIRY = [["7", "7 days"], ["30", "30 days"], ["90", "90 days"], ["never", "Never"]];
const ib = "h-8 px-2.5 rounded-md text-xs flex items-center gap-1.5 border border-slate-700 text-slate-200 hover:border-cyan-500 hover:text-cyan-300 transition-colors";

const LinkCard = ({ link, name, open, onToggle, onRevoke }) => {
  const url = shareUrl(link.share_id), expired = link.status === "expired";
  const copy = async () => { await copyText(url); toast.success("Link copied"); };
  return (
    <div className={`rounded-md border p-3 space-y-2 ${expired ? "border-slate-800 opacity-70" : "border-slate-700 bg-[#0d1220]"}`} data-testid={`share-link-${link.share_id}`}>
      <div className="flex items-center gap-2">
        <Link2 size={14} className="text-cyan-400 shrink-0" />
        <span className="font-mono text-[11px] text-slate-300 truncate flex-1" data-testid="share-link-url">{url}</span>
        <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${expired ? "bg-rose-500/15 text-rose-300" : "bg-emerald-500/15 text-emerald-300"}`} data-testid="share-link-status">{expired ? "expired" : "active"}</span>
      </div>
      <p className="text-[11px] text-slate-500" data-testid="share-link-expiry">{expiryText(link)} · by {link.created_by} · {timeAgo(link.created_at)} · <Eye size={11} className="inline -mt-0.5" /> {link.views} view{link.views === 1 ? "" : "s"}</p>
      <div className="flex flex-wrap gap-1.5">
        {!expired && <>
          <button className={ib} onClick={copy} data-testid="share-copy-btn"><Copy size={13} />Copy</button>
          <a className={ib} href={whatsappUrl(url, name)} target="_blank" rel="noreferrer" data-testid="share-whatsapp-btn"><MessageCircle size={13} />WhatsApp</a>
          <button className={`${ib} ${open ? "border-cyan-500 text-cyan-300" : ""}`} onClick={onToggle} data-testid="share-qr-toggle"><QrCode size={13} />QR</button>
          <a className={ib} href={url} target="_blank" rel="noreferrer" data-testid="share-open-btn"><ExternalLink size={13} />Open</a>
        </>}
        <button className={`${ib} ml-auto hover:!border-rose-500 hover:!text-rose-300`} onClick={onRevoke} data-testid="share-revoke-btn"><Trash2 size={13} />{expired ? "Remove" : "Revoke"}</button>
      </div>
      {open && !expired && (
        <div className="flex items-center gap-4 pt-1" data-testid="share-qr">
          <div className="bg-white p-2 rounded-md"><QRCodeSVG value={url} size={148} level="M" /></div>
          <p className="text-xs text-slate-400">Let your client scan this with the phone camera to open the 3D model.</p>
        </div>
      )}
    </div>
  );
};

export const ShareDialog = ({ projectId, projectName, onClose }) => {
  const [links, setLinks] = useState(null);
  const [days, setDays] = useState("30");
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = () => api.get(`/projects/${projectId}/shares`).then(({ data }) => setLinks(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/shares`, { days: days === "never" ? null : Number(days) });
      setLinks((l) => [data, ...(l || [])]); setOpenId(data.share_id);
      await copyText(shareUrl(data.share_id));
      toast.success("View-only 3D link created and copied");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const revoke = async (l) => {
    if (l.status !== "expired" && !window.confirm("Revoke this link? Anyone using it will lose access.")) return;
    try { await api.delete(`/projects/${projectId}/shares/${l.share_id}`); setLinks((xs) => xs.filter((x) => x.share_id !== l.share_id)); toast.success("Link revoked"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#111827] border-slate-700 text-slate-100 max-h-[90vh] overflow-y-auto" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Share 3D with your client</DialogTitle>
          <DialogDescription className="text-slate-400">A view-only link — no login needed. Clients can spin every item of this project on their phone. It always shows the latest saved design.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-400">Link expires after</p>
          <div className="flex gap-1.5" data-testid="share-expiry-group">
            {EXPIRY.map(([v, l]) => (
              <button key={v} onClick={() => setDays(v)} data-testid={`share-expiry-${v}`}
                className={`flex-1 h-9 rounded-md text-xs font-semibold border transition-colors ${days === v ? "border-cyan-500 bg-cyan-500/10 text-cyan-300" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>{l}</button>
            ))}
          </div>
          <button onClick={create} disabled={busy} data-testid="share-create-btn"
            className="w-full h-10 rounded-md bg-cyan-500 text-slate-950 font-semibold text-sm flex items-center justify-center gap-2 hover:bg-cyan-400 transition-colors disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />}Create view-only link
          </button>
        </div>
        <div className="space-y-2" data-testid="share-links-list">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-400">Links for this project</p>
          {!links ? <Loader2 className="animate-spin text-cyan-400 mx-auto" size={18} />
            : !links.length ? <p className="text-xs text-slate-500" data-testid="share-links-empty">No links yet.</p>
            : links.map((l) => <LinkCard key={l.share_id} link={l} name={projectName} open={openId === l.share_id} onToggle={() => setOpenId(openId === l.share_id ? null : l.share_id)} onRevoke={() => revoke(l)} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
};
